import { isNil } from 'lodash-es';

import type { StripeRecurringInterval } from '@repo/api-codegen/v2';
import { useSuspenseGetBillingInfo } from '@repo/api-codegen/v2';
import { usePlan } from '../../../hooks/usePlans';

// Types
export interface ITier {
	flat_amount?: number | string | null;
	flat_amount_decimal?: number | string | null;
	unit_amount?: number | null;
	unit_amount_decimal?: number | string | null;
	up_to?: number | string | null;
}

interface IBracket {
	bracketStart: number;
	bracketEnd: number;
	existingSeatCountInThisBracket: number;
	newSeatCountInThisBracket: number;
	costPerSeat: number;
	increaseInCost: number;
}

interface ICostCalculationResult {
	brackets: IBracket[];
	interval: StripeRecurringInterval;
	currentCost: number;
	newCost: number;
}

interface IUseCalculateCostArgs {
	type: 'viewers' | 'editors' | 'integrations';
	nominalCurrentCount: number;
	nominalNewCount: number;
}

/**
 * Sorts tiers by their `up_to` values in ascending order.
 *
 * Sorting logic:
 * - Tiers with `up_to: null` (unlimited tiers) are placed at the end
 * - Tiers with numeric `up_to` values are sorted in ascending order
 * - Handles both string and number types for `up_to` values
 * - Returns empty array for null/empty input
 *
 * Example: [10, null, 5, 20] → [5, 10, 20, null]
 */
export const sortTiers = (tiers: ITier[] | null | undefined): ITier[] => {
	if (isNil(tiers) || tiers.length === 0) return [];

	return tiers.sort((a, b) => {
		if (isNil(a.up_to) && isNil(b.up_to)) return 0;
		if (isNil(a.up_to)) return 1;
		if (isNil(b.up_to)) return -1;

		const aValue =
			typeof a.up_to === 'string' ? parseInt(a.up_to, 10) : (a.up_to ?? 0);
		const bValue =
			typeof b.up_to === 'string' ? parseInt(b.up_to, 10) : (b.up_to ?? 0);

		return aValue - bValue;
	});
};

/**
 * Parses tier data to extract seats included in plan
 */
export const parseSeatsIncludedInPlan = (
	tiers: ITier[] | undefined | null
): number | null | undefined => {
	if (isNil(tiers)) return undefined;

	const firstTierWithLimit = sortTiers(tiers).find(
		(tier) => !isNil(tier.up_to)
	);
	if (!firstTierWithLimit?.up_to) return null;

	return parseInt(firstTierWithLimit.up_to.toString(), 10);
};

/**
 * Calculates cost brackets for additional plan resources
 */
export const calculateCostBrackets = (
	tiers: ITier[],
	nominalCurrentCount: number,
	nominalNewCount: number
): IBracket[] => {
	const sortedTiers = sortTiers(tiers);
	const brackets: IBracket[] = [];

	sortedTiers.forEach((tier, index) => {
		const isFirstTier = index === 0;
		const previousTierLimit = isFirstTier
			? 0
			: parseInt(sortedTiers[index - 1].up_to?.toString() ?? '0', 10);

		const currentTierLimit = tier.up_to
			? parseInt(tier.up_to.toString(), 10)
			: Infinity;

		const existingSeatCountInThisBracket = Math.max(
			0,
			Math.min(nominalCurrentCount, currentTierLimit) - previousTierLimit
		);
		const newSeatCountInThisBracket = Math.max(
			0,
			Math.min(nominalNewCount, currentTierLimit) - previousTierLimit
		);
		const costPerSeat = Math.max(tier.unit_amount ?? 0, 0);

		brackets.push({
			bracketStart: previousTierLimit + 1,
			bracketEnd: currentTierLimit,
			existingSeatCountInThisBracket,
			newSeatCountInThisBracket,
			costPerSeat,
			increaseInCost:
				costPerSeat *
				(newSeatCountInThisBracket - existingSeatCountInThisBracket),
		});
	});

	return brackets;
};

export function useCalculateCost({
	type,
	nominalCurrentCount,
	nominalNewCount,
}: IUseCalculateCostArgs): ICostCalculationResult {
	const { data } = useSuspenseGetBillingInfo({});

	const tierMap = {
		viewers: data?.viewers_limit ?? [],
		editors: data?.editors_limit ?? [],
		integrations: data?.integrations_limit ?? [],
	};

	const tiers = tierMap[type] ?? [];
	const brackets = calculateCostBrackets(
		tiers,
		nominalCurrentCount,
		nominalNewCount
	);

	const currentCost = brackets.reduce(
		(acc, bracket) =>
			acc + bracket.costPerSeat * bracket.existingSeatCountInThisBracket,
		0
	);

	const newCost = brackets.reduce(
		(acc, bracket) =>
			acc + bracket.costPerSeat * bracket.newSeatCountInThisBracket,
		0
	);

	return {
		brackets,
		currentCost,
		newCost,
		interval: data?.interval ?? 'month',
	};
}

export function useLimits() {
	const { data, isLoading } = useSuspenseGetBillingInfo(
		{},
		{
			refetchInterval: 1000 * 60 * 5, // 5 minutes
		}
	);

	const { plan } = usePlan();

	// Parse seats included in plan
	const editorSeatsIncludedInPlan = parseSeatsIncludedInPlan(
		data?.editors_limit
	);
	const viewerSeatsIncludedInPlan = parseSeatsIncludedInPlan(
		data?.viewers_limit
	);
	const integrationSeatsIncludedInPlan = parseSeatsIncludedInPlan(
		data?.integrations_limit
	);
	const extensionSeatsIncludedInPlan = parseSeatsIncludedInPlan(
		data?.extensions_limit
	);

	// Calculate total seats (plan fallback)
	const editorOrAdminSeats = editorSeatsIncludedInPlan ?? plan?.editors ?? 0;
	const viewerSeats = viewerSeatsIncludedInPlan ?? plan?.viewers ?? 0;
	const integrationSeats =
		integrationSeatsIncludedInPlan ?? plan?.integrations ?? 0;
	const extensionSeats = extensionSeatsIncludedInPlan ?? plan?.extensions ?? 0;

	// Get used seats from v2 billing/info endpoint
	const usedEditorOrAdminSeats = data?.used_editor_seats ?? 0;
	const usedViewerSeats = data?.used_viewer_seats ?? 0;
	const usedIntegrationSeats = data?.used_integration_seats ?? 0;
	const usedExtensionSeats = data?.used_extension_seats ?? 0;
	const exemptEditorSeats = data?.exempt_editor_seats ?? 0;

	return {
		// Used seats
		usedEditorOrAdminSeats,
		usedViewerSeats,
		usedIntegrationSeats,
		usedExtensionSeats,
		exemptEditorSeats,

		// Total seats
		editorOrAdminSeats,
		viewerSeats,
		integrationSeats,
		extensionSeats,

		// Trial limits
		trialIntegrationsLimit: data?.trial_integrations_limit,
		trialExtensionsLimit: data?.trial_extensions_limit,

		// Plan info
		planName: data?.plan_name,
		isLoading,
	};
}

// Legacy exports for backward compatibility
export const parseLimitData = (tier: ITier[] | undefined | null) => ({
	seatsIncludedInPlan: parseSeatsIncludedInPlan(tier),
});
